import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import {
  Upload, FileText, Layers, ChevronDown, ChevronRight, Loader2,
  CheckCircle2, AlertTriangle, Clock, Search, Filter, Eye, Edit2,
  RefreshCw, Trash2, ArrowRight, Wand2, GitBranch, MapPin
} from 'lucide-react';

const DISCIPLINE_COLORS = {
  Architectural: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
  Mechanical: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800',
  Electrical: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  Plumbing: 'bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-800',
  Structural: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
  Civil: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800',
  'Fire Protection': 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800',
  Landscape: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  Unclassified: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-muted/30 dark:text-muted-foreground dark:border-border',
};

const STATUS_CONFIG = {
  processing: { label: 'Processing', color: 'text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800', icon: Loader2, spin: true },
  classifying: { label: 'Classifying', color: 'text-blue-700 bg-blue-100 border-blue-300 dark:text-blue-300 dark:bg-blue-950/40 dark:border-blue-800', icon: Loader2, spin: true },
  active: { label: 'Active', color: 'text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800', icon: CheckCircle2 },
  superseded: { label: 'Superseded', color: 'text-gray-600 bg-gray-100 border-gray-300 dark:text-muted-foreground dark:bg-muted/30 dark:border-border', icon: Clock },
  error: { label: 'Error', color: 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800', icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.processing;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded-sm border ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function DisciplineBadge({ discipline }) {
  const d = discipline || 'Unclassified';
  const cls = DISCIPLINE_COLORS[d] || DISCIPLINE_COLORS.Unclassified;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded-sm border ${cls}`}>
      {d}
    </span>
  );
}

function SheetRow({ sheet, setId, projectId, onReclassify }) {
  const [editing, setEditing] = useState(false);
  const [discipline, setDiscipline] = useState(sheet.discipline || '');
  const [pageType, setPageType] = useState(sheet.page_type || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/closeout/projects/${projectId}/drawing-sets/${setId}/sheets/${sheet.id}/classify`, {
        discipline: discipline || null,
        page_type: pageType || null,
      });
      setEditing(false);
      onReclassify?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2 hover:bg-muted/20 transition-colors border-b border-border/30 last:border-0" data-testid={`sheet-row-${sheet.id}`}>
      <span className="text-[10px] font-mono text-muted-foreground w-8 text-right shrink-0">
        {sheet.page_number}
      </span>
      <span className="text-xs font-mono text-foreground/80 w-16 shrink-0">
        {sheet.sheet_number || '—'}
      </span>
      <span className="text-xs text-foreground/70 flex-1 truncate">
        {sheet.sheet_name}
      </span>
      {editing ? (
        <div className="flex items-center gap-2">
          <select value={discipline} onChange={e => setDiscipline(e.target.value)}
            className="text-[10px] bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-foreground">
            <option value="">Unclassified</option>
            {Object.keys(DISCIPLINE_COLORS).filter(d => d !== 'Unclassified').map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select value={pageType} onChange={e => setPageType(e.target.value)}
            className="text-[10px] bg-secondary border border-border rounded-sm px-1.5 py-0.5 text-foreground">
            <option value="">—</option>
            <option value="floor_plan">Floor Plan</option>
            <option value="schedule">Schedule</option>
            <option value="detail">Detail</option>
            <option value="elevation">Elevation</option>
            <option value="section">Section</option>
            <option value="cover">Cover</option>
            <option value="other">Other</option>
          </select>
          <button onClick={handleSave} disabled={saving}
            className="text-[10px] px-2 py-0.5 bg-primary/20 text-primary border border-primary/30 rounded-sm hover:bg-primary/30 transition-colors disabled:opacity-50">
            {saving ? 'Saving' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="text-[10px] text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      ) : (
        <>
          <DisciplineBadge discipline={sheet.discipline} />
          {sheet.page_type && (
            <span className="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 bg-secondary/50 rounded-sm border border-border/50">
              {sheet.page_type}
            </span>
          )}
          {sheet.has_schedule_data && (
            <span className="text-[10px] font-mono text-violet-700 px-1.5 py-0.5 bg-violet-100 rounded-sm border border-violet-300 dark:text-violet-300 dark:bg-violet-950/30 dark:border-violet-800">
              HAS DATA
            </span>
          )}
          {!sheet.is_current && sheet.is_current !== undefined && (
            <span className="text-[10px] font-mono text-red-600/80 px-1.5 py-0.5 bg-red-50 rounded-sm border border-red-200 line-through dark:text-red-400/60 dark:bg-red-950/20 dark:border-red-900/30">
              SUPERSEDED
            </span>
          )}
          {sheet.supersedes_sheet_id && (
            <ArrowRight className="w-3 h-3 text-emerald-500" title="Supersedes previous revision" />
          )}
          <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground p-1 rounded-sm hover:bg-muted/30 transition-colors"
            data-testid={`edit-sheet-${sheet.id}`}>
            <Edit2 className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}

function SetCard({ set, projectId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  const fetchDetail = async () => {
    if (detail) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/drawing-sets/${set.id}`);
      setDetail(res.data);
      setExpanded(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete drawing set "${set.name}"? Sheets will be removed from the active set.`)) return;
    try {
      await api.delete(`/closeout/projects/${projectId}/drawing-sets/${set.id}`);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReclassify = async (forceAll = false) => {
    setReclassifying(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/drawing-sets/${set.id}/reclassify`, { force_all: forceAll });
      const { toast } = await import('sonner');
      toast.success(res.data.message || 'Reclassification started');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/closeout/projects/${projectId}/drawing-sets/${set.id}`);
          if (r.data.reclassify_status !== 'running') {
            clearInterval(poll);
            setReclassifying(false);
            setDetail(r.data);
            onRefresh();
          }
        } catch { clearInterval(poll); setReclassifying(false); }
      }, 3000);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Reclassification failed');
      setReclassifying(false);
    }
  };

  return (
    <div className={`border rounded-sm transition-colors ${set.status === 'superseded' ? 'border-border/50 opacity-60' : 'border-border bg-card'}`}
      data-testid={`drawing-set-${set.id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={fetchDetail} className="text-muted-foreground hover:text-foreground" data-testid={`expand-set-${set.id}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
            expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Layers className="w-4 h-4 text-primary/60 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{set.name}</span>
            <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded-sm border border-border/50">
              {set.revision_label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{set.file_name}</span>
            <span className="text-[10px] text-muted-foreground">{set.sheet_count || 0} sheets</span>
            {set.matched_sheets > 0 && (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400">{set.matched_sheets} matched</span>
            )}
            {set.new_sheets > 0 && (
              <span className="text-[10px] text-blue-600 dark:text-blue-400">{set.new_sheets} new</span>
            )}
          </div>
        </div>
        <StatusBadge status={set.status} />
        {set.status === 'active' && (
          <button onClick={() => handleReclassify(false)} disabled={reclassifying}
            title="AI Reclassify unclassified sheets" data-testid={`reclassify-set-${set.id}`}
            className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 text-violet-700 bg-violet-100 border border-violet-300 dark:text-violet-300 dark:bg-violet-950/30 dark:border-violet-800 rounded-sm hover:bg-violet-200 dark:hover:bg-violet-950/50 transition-colors disabled:opacity-50">
            {reclassifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            {reclassifying ? 'Classifying...' : 'AI Reclassify'}
          </button>
        )}
        <span className="text-[10px] text-muted-foreground">
          {new Date(set.created_at).toLocaleDateString()}
        </span>
        <button onClick={handleDelete} className="text-muted-foreground/50 hover:text-red-400 p-1 rounded-sm transition-colors"
          data-testid={`delete-set-${set.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && detail && (
        <div className="border-t border-border/50">
          {/* Classification summary */}
          {detail.type_counts && Object.keys(detail.type_counts).length > 0 && (
            <div className="px-4 py-2 bg-secondary/10 border-b border-border/30">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Classification:</span>
                {Object.entries(detail.type_counts).map(([type, count]) => (
                  <span key={type} className="text-[10px] font-mono text-foreground/70 bg-secondary/50 px-1.5 py-0.5 rounded-sm border border-border/50">
                    {type}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Sheets by discipline */}
          {detail.by_discipline && Object.keys(detail.by_discipline).length > 0 ? (
            Object.entries(detail.by_discipline).map(([disc, sheets]) => (
              <div key={disc}>
                <div className="px-4 py-1.5 bg-muted/10 border-b border-border/30">
                  <DisciplineBadge discipline={disc} />
                  <span className="text-[10px] text-muted-foreground ml-2">{sheets.length} sheets</span>
                </div>
                {sheets.map(sh => (
                  <SheetRow key={sh.id} sheet={sh} setId={set.id} projectId={projectId} onReclassify={onRefresh} />
                ))}
              </div>
            ))
          ) : (
            <div className="px-4 py-6 text-center">
              <p className="text-xs text-muted-foreground">
                {detail.status === 'processing' || detail.status === 'classifying'
                  ? 'AI is classifying sheets...'
                  : 'No sheets found'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DrawingSetsPanel({ projectId, onViewOnFloorPlan }) {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [view, setView] = useState('sets'); // 'sets' | 'sheets' | 'timeline'
  const [currentSheets, setCurrentSheets] = useState(null);
  const [filterDisc, setFilterDisc] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [ingested, setIngested] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [linkedDrawings, setLinkedDrawings] = useState(null);

  const handleOpenOnFloorPlan = async (sheetId) => {
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/set-master`, { sheet_id: sheetId });
    } catch { /* non-critical */ }
    onViewOnFloorPlan?.(sheetId);
  };

  const fetchIngested = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/drawing-sets/ingested-drawings`);
      setIngested(res.data.drawings || []);
    } catch { /* non-critical */ }
  }, [projectId]);

  const handleProcessIntake = async (intakeId) => {
    setProcessingId(intakeId);
    try {
      await api.post(`/closeout/projects/${projectId}/drawing-sets/from-intake`,
        new URLSearchParams({ intake_id: intakeId }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      const { toast } = await import('sonner');
      toast.success('Processing drawing into sheets — this runs in the background');
      await Promise.all([fetchIngested(), fetchSets()]);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Could not start processing');
    } finally {
      setProcessingId(null);
    }
  };

  const fetchSets = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/drawing-sets`);
      setSets(res.data);
      return res.data;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Auto-backfill removed: it was not idempotent (created duplicate sets),
  // ran a multi-minute blocking job, and is superseded by the explicit
  // "Ingested Drawings — Process" action below.

  const fetchCurrentSheets = useCallback(async () => {
    try {
      const url = filterDisc
        ? `/closeout/projects/${projectId}/drawing-sets/current-sheets?discipline=${filterDisc}`
        : `/closeout/projects/${projectId}/drawing-sets/current-sheets`;
      const res = await api.get(url);
      setCurrentSheets(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [projectId, filterDisc]);

  useEffect(() => { fetchSets(); }, [fetchSets]);
  useEffect(() => { fetchIngested(); }, [fetchIngested]);
  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/linked-drawings`)
      .then(r => setLinkedDrawings(r.data))
      .catch(() => {});
  }, [projectId]);
  useEffect(() => { if (view === 'sheets') fetchCurrentSheets(); }, [view, fetchCurrentSheets]);
  useEffect(() => {
    if (view === 'timeline') {
      api.get(`/closeout/projects/${projectId}/drawing-sets/timeline`)
        .then(r => setTimeline(r.data))
        .catch(() => {});
    }
  }, [view, projectId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"]');
    const file = fileInput?.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', uploadName || '');
      fd.append('revision_label', uploadLabel || '');
      await api.post(`/closeout/projects/${projectId}/drawing-sets/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { toast } = await import('sonner');
      toast.success('Drawing set uploaded — AI classification in progress');
      setShowUploadForm(false);
      setUploadName('');
      setUploadLabel('');
      fetchSets();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Auto-refresh sets that are still processing
  useEffect(() => {
    const processing = sets.some(s => s.status === 'processing' || s.status === 'classifying');
    if (!processing) return;
    const interval = setInterval(() => { fetchSets(); fetchIngested(); }, 5000);
    return () => clearInterval(interval);
  }, [sets, fetchSets, fetchIngested]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="drawing-sets-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Drawing Sets
          </h2>
          {/* View toggle */}
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button onClick={() => setView('sets')} data-testid="view-sets-btn"
              className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${view === 'sets' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              By Set
            </button>
            <button onClick={() => setView('sheets')} data-testid="view-sheets-btn"
              className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${view === 'sheets' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              All Current Sheets
            </button>
            <button onClick={() => setView('timeline')} data-testid="view-timeline-btn"
              className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${view === 'timeline' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              Revision Timeline
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchSets} className="text-muted-foreground hover:text-foreground p-1.5 rounded-sm hover:bg-muted/30 transition-colors"
            data-testid="refresh-sets-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setShowUploadForm(!showUploadForm)} data-testid="upload-drawing-set-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Upload Set
          </button>
        </div>
      </div>

      {/* Linked Drawings gallery — surfaces one-click BUILD-job drawings */}
      {view === 'sets' && linkedDrawings?.drawings?.length > 0 && (
        <div className="mb-6" data-testid="linked-drawings-gallery">
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Linked Drawings
            </h3>
            <span className="text-[10px] font-mono text-muted-foreground">
              {linkedDrawings.total_sheets} sheets · {linkedDrawings.total_pins} located assets
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Extracted from the drawings this project was built from. Click any sheet to open it on the Floor Plan with its asset pins.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {linkedDrawings.drawings.map(d => (
              <button key={d.sheet_id}
                onClick={() => handleOpenOnFloorPlan(d.sheet_id)}
                data-testid={`linked-drawing-${d.sheet_id}`}
                className="group text-left bg-card border border-border rounded-md overflow-hidden hover:border-primary/40 hover:shadow-md transition-all">
                <div className="relative aspect-[4/3] bg-muted/30 overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${d.image_url}`}
                    alt={d.sheet_name} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                  {d.pin_count > 0 && (
                    <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono rounded-sm bg-blue-600/90 text-white">
                      <MapPin className="w-3 h-3" /> {d.pin_count}
                    </span>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs font-mono text-foreground/90 truncate">{d.sheet_number || '—'}</span>
                    {d.trade && <DisciplineBadge discipline={d.trade} />}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{d.sheet_name || 'Untitled sheet'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <form onSubmit={handleUpload} className="mb-4 p-4 bg-card border border-primary/20 rounded-sm space-y-3" data-testid="upload-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Set Name (optional)</label>
              <input type="text" value={uploadName} onChange={e => setUploadName(e.target.value)}
                placeholder="e.g. CD Set - Phase 2" data-testid="upload-set-name"
                className="mt-1 w-full px-3 py-1.5 text-xs bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Revision Label (optional)</label>
              <input type="text" value={uploadLabel} onChange={e => setUploadLabel(e.target.value)}
                placeholder="e.g. Rev 3, ASI-005" data-testid="upload-revision-label"
                className="mt-1 w-full px-3 py-1.5 text-xs bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Drawing Set PDF</label>
            <input type="file" accept=".pdf" required data-testid="upload-file-input"
              className="mt-1 w-full text-xs text-muted-foreground file:mr-3 file:px-3 file:py-1.5 file:text-xs file:bg-secondary file:border file:border-border file:rounded-sm file:text-foreground file:cursor-pointer" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={uploading} data-testid="submit-upload-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
              {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...</> : <><Upload className="w-3.5 h-3.5" /> Upload & Classify</>}
            </button>
            <button type="button" onClick={() => setShowUploadForm(false)}
              className="px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Ingested drawings not yet processed into sheets */}
      {ingested.some(d => !d.processed) && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/10 border border-amber-300 dark:border-amber-800/40 rounded-sm" data-testid="ingested-drawings-section">
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <p className="text-xs font-heading uppercase tracking-wider text-amber-700 dark:text-amber-400">
              Ingested Drawings — Process into Sheets
            </p>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            These drawings were uploaded via Smart Intake but not yet rendered into sheets. Process them to make their pages available as floor plans (no re-upload needed).
          </p>
          <div className="space-y-1.5">
            {ingested.filter(d => !d.processed).map(d => (
              <div key={d.id} className="flex items-center gap-3 px-3 py-2 bg-card border border-border rounded-sm text-xs" data-testid={`ingested-drawing-${d.id}`}>
                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate text-foreground/80">{d.filename}</span>
                <button
                  onClick={() => handleProcessIntake(d.id)}
                  disabled={processingId === d.id}
                  data-testid={`process-ingested-${d.id}`}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-400/40 rounded-sm hover:bg-amber-500/25 transition-colors disabled:opacity-50">
                  {processingId === d.id
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Processing...</>
                    : <><ArrowRight className="w-3 h-3" /> Process</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sets View */}
      {view === 'sets' ? (
        sets.length > 0 ? (
          <div className="space-y-2">
            {sets.map(s => (
              <SetCard key={s.id} set={s} projectId={projectId} onRefresh={fetchSets} />
            ))}
          </div>
        ) : (
          (linkedDrawings?.drawings?.length > 0) ? (
            <div className="text-center py-4 text-[11px] text-muted-foreground" data-testid="no-native-sets-note">
              Your drawings are shown above as <span className="text-foreground/70">Linked Drawings</span>. Upload a set here only if you want revision tracking (compare CD vs ASI vs as-built).
            </div>
          ) : (
          <div className="flex flex-col items-center justify-center h-48 bg-card border border-dashed border-border rounded-sm" data-testid="empty-sets">
            <Layers className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No drawing sets uploaded</p>
            <p className="text-xs text-muted-foreground/60 mb-4 max-w-sm text-center">
              Upload a construction drawing PDF to auto-classify sheets by discipline, track revisions, and flag superseded pages.
            </p>
            <button onClick={() => setShowUploadForm(true)} data-testid="empty-upload-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Upload First Set
            </button>
          </div>
          )
        )
      ) : view === 'timeline' ? (
        /* Revision Timeline View */
        <div data-testid="revision-timeline">
          {timeline?.timeline?.length > 0 ? (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-0">
                {timeline.timeline.map((rev, idx) => {
                  const isLast = idx === timeline.timeline.length - 1;
                  const discEntries = Object.entries(rev.discipline_summary || {}).sort((a,b) => b[1] - a[1]);
                  return (
                    <div key={rev.id} className="relative flex gap-4 pb-6" data-testid={`timeline-rev-${rev.id}`}>
                      {/* Dot */}
                      <div className={`w-3 h-3 rounded-full shrink-0 mt-1 z-10 border-2 ${
                        rev.status === 'active'
                          ? 'bg-emerald-500 border-emerald-300 dark:border-emerald-700'
                          : rev.status === 'superseded'
                            ? 'bg-gray-400 border-gray-300 dark:bg-gray-600 dark:border-gray-500'
                            : 'bg-amber-500 border-amber-300 dark:border-amber-700'
                      }`} style={{ marginLeft: '18px' }} />
                      {/* Card */}
                      <div className={`flex-1 border rounded-sm p-3 ${rev.status === 'superseded' ? 'border-border/50 bg-muted/20 opacity-70' : 'border-border bg-card'}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{rev.name}</span>
                            <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded-sm border border-border/50">
                              {rev.revision_label}
                            </span>
                            <StatusBadge status={rev.status} />
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(rev.created_at).toLocaleDateString()} {rev.created_by && `by ${rev.created_by}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-[10px]">
                          <span className="text-muted-foreground">{rev.total_sheets} sheets</span>
                          {rev.new_sheets > 0 && <span className="text-blue-600 dark:text-blue-400">{rev.new_sheets} new</span>}
                          {rev.matched_sheets > 0 && <span className="text-emerald-600 dark:text-emerald-400">{rev.matched_sheets} matched revisions</span>}
                          {rev.superseded_sheets > 0 && <span className="text-red-500/70">{rev.superseded_sheets} superseded</span>}
                        </div>
                        {discEntries.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {discEntries.slice(0, 6).map(([disc, count]) => (
                              <DisciplineBadge key={disc} discipline={disc} />
                            ))}
                            {discEntries.length > 6 && (
                              <span className="text-[10px] text-muted-foreground">+{discEntries.length - 6} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No revision history yet. Upload drawing sets to start tracking revisions.
            </div>
          )}
        </div>
      ) : (
        /* Current Sheets View */
        <div>
          {/* Discipline filter */}
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <select value={filterDisc} onChange={e => setFilterDisc(e.target.value)} data-testid="discipline-filter"
              className="text-[10px] bg-secondary border border-border rounded-sm px-2 py-1 text-foreground">
              <option value="">All Disciplines</option>
              {Object.keys(DISCIPLINE_COLORS).filter(d => d !== 'Unclassified').map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {currentSheets && (
              <span className="text-[10px] text-muted-foreground">
                {currentSheets.sheets?.length || 0} current sheets
              </span>
            )}
          </div>
          {currentSheets?.by_discipline && Object.keys(currentSheets.by_discipline).length > 0 ? (
            Object.entries(currentSheets.by_discipline).map(([disc, sheets]) => (
              <div key={disc} className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <DisciplineBadge discipline={disc} />
                  <span className="text-[10px] text-muted-foreground">{sheets.length} sheets</span>
                </div>
                <div className="border border-border rounded-sm bg-card">
                  {sheets.map(sh => (
                    <div key={sh.id} className="flex items-center gap-3 px-3 py-2 border-b border-border/30 last:border-0 hover:bg-muted/10">
                      <span className="text-xs font-mono text-foreground/80 w-16">{sh.sheet_number || '—'}</span>
                      <span className="text-xs text-foreground/70 flex-1 truncate">{sh.sheet_name}</span>
                      {sh.page_type && (
                        <span className="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 bg-secondary/50 rounded-sm border border-border/50">
                          {sh.page_type}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-xs text-muted-foreground" data-testid="no-sheets">
              No current sheets found. Upload a drawing set to get started.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
